import {
  ApiError,
  deleteSmsTemplates,
  getOrgBranding,
  getSmsTemplates,
  upsertSmsTemplates,
} from '@jq4race/api-client';
import { Trans, useTranslation } from '@jq4race/i18n';
import type { SmsTemplatesResponse } from '@jq4race/shared';
import { Button, Card, CardContent, Label } from '@jq4race/ui';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// ── Preview helpers ────────────────────────────────────────────────────────────

const PREVIEW_LINK = 'https://jq4.io/t/abc123';

function renderPreview(template: string, venueName: string): string {
  const vars: Record<string, string> = {
    '{{venue_name}}': venueName,
    // Backward-compat alias: preview legacy templates that still use the old token.
    '{{nombre_establecimiento}}': venueName,
    '{{link_ticket}}': PREVIEW_LINK,
  };
  return Object.entries(vars).reduce((acc, [key, val]) => acc.replaceAll(key, val), template);
}

const SMS_MAX_CHARS = 160;
const LINK_TICKET_VAR = '{{link_ticket}}';

function missingLinkTicket(text: string): boolean {
  return !text.includes(LINK_TICKET_VAR);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SmsTemplatesPage() {
  const { t } = useTranslation('org-sms-templates');
  const [templates, setTemplates] = useState<SmsTemplatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  // Form state
  const [textEn, setTextEn] = useState('');
  const [textEs, setTextEs] = useState('');

  // Preview sample: the current organization's display name (best-effort).
  const [orgName, setOrgName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getOrgBranding()
      .then((b) => {
        if (!cancelled) setOrgName(b.name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSmsTemplates();
      setTemplates(data);
      setTextEn(data.en ?? '');
      setTextEs(data.es ?? '');
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner after 5 s
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setErrorBanner(null);
    setSaving(true);
    try {
      const saved = await upsertSmsTemplates({ en: textEn, es: textEs });
      setTemplates(saved);
      setSuccess(t('saveSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('saveError') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('genericError') }));
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleRestoreDefault() {
    const confirmed = window.confirm(t('restoreConfirm'));
    if (!confirmed) return;

    setErrorBanner(null);
    setRestoring(true);
    try {
      const restored = await deleteSmsTemplates();
      setTemplates(restored);
      setTextEn(restored.en ?? '');
      setTextEs(restored.es ?? '');
      setSuccess(t('restoreSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('restoreError') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('genericError') }));
      }
    } finally {
      setRestoring(false);
    }
  }

  // Validation
  const enInvalid = missingLinkTicket(textEn);
  const esInvalid = missingLinkTicket(textEs);
  // QA-I10: an empty locale is its own, explained, state - both are required.
  const enEmpty = textEn.trim().length === 0;
  const esEmpty = textEs.trim().length === 0;
  const canSave = !enInvalid && !esInvalid && !enEmpty && !esEmpty;

  // Preview
  const venueName = orgName?.trim() ? orgName : t('previewVenueFallback');
  const previewEn = renderPreview(textEn, venueName);
  const previewEs = renderPreview(textEs, venueName);
  const charCountEn = previewEn.length;
  const charCountEs = previewEs.length;

  const busy = saving || restoring;

  return (
    <div className="space-y-6">
      <p className="max-w-xl text-[14px] leading-relaxed text-jq-graphite">
        <Trans
          t={t}
          i18nKey="intro"
          values={{ venueName: '{{venue_name}}', linkTicket: '{{link_ticket}}' }}
          components={{
            code1: <code className="font-mono" />,
            code2: <code className="font-mono" />,
          }}
        />
      </p>

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <form onSubmit={handleSave} className="space-y-8">
              {/* English template */}
              <TemplateField
                id="sms-template-en"
                locale={t('locales.en')}
                value={textEn}
                onChange={setTextEn}
                preview={previewEn}
                charCount={charCountEn}
                invalid={enInvalid}
                empty={enEmpty}
                disabled={busy}
              />

              {/* Spanish template */}
              <TemplateField
                id="sms-template-es"
                locale={t('locales.es')}
                value={textEs}
                onChange={setTextEs}
                preview={previewEs}
                charCount={charCountEs}
                invalid={esInvalid}
                empty={esEmpty}
                disabled={busy}
              />

              {/* Action row */}
              <div className="flex items-center justify-between gap-3 border-t border-jq-sand/70 pt-6">
                {templates?.isCustom ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => void handleRestoreDefault()}
                  >
                    {restoring ? t('restoring') : t('restoreDefault')}
                  </Button>
                ) : (
                  <span className="text-[12px] text-jq-graphite">{t('usingDefault')}</span>
                )}
                <div className="flex flex-col items-end gap-1.5">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!canSave || busy}
                    aria-describedby={!canSave ? 'sms-templates-save-hint' : undefined}
                  >
                    {saving ? t('saving') : t('saveTemplates')}
                  </Button>
                  {!canSave && (
                    <p
                      id="sms-templates-save-hint"
                      className="max-w-xs text-right text-[12px] text-jq-graphite"
                    >
                      {t('saveBlockedHint')}
                    </p>
                  )}
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Template field with preview and char count ────────────────────────────────

interface TemplateFieldProps {
  id: string;
  locale: string;
  value: string;
  onChange: (v: string) => void;
  preview: string;
  charCount: number;
  invalid: boolean;
  empty: boolean;
  disabled: boolean;
}

function TemplateField({
  id,
  locale,
  value,
  onChange,
  preview,
  charCount,
  invalid,
  empty,
  disabled,
}: TemplateFieldProps) {
  const { t } = useTranslation('org-sms-templates');
  const overLimit = charCount > SMS_MAX_CHARS;

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
          {locale}
        </Label>
        <p className="text-[12px] text-jq-graphite">
          <Trans
            t={t}
            i18nKey="field.helpText"
            values={{ venueName: '{{venue_name}}', linkTicket: '{{link_ticket}}' }}
            components={{
              code1: <code className="font-mono" />,
              code2: <code className="font-mono" />,
            }}
          />
        </p>
      </div>

      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={4}
        maxLength={500}
        placeholder={t('field.placeholder', { locale })}
        aria-invalid={invalid || empty}
        aria-describedby={`${id}-error ${id}-preview ${id}-charcount`}
        className="w-full resize-y rounded-md border border-jq-sand/70 bg-jq-surface-1 px-3 py-2 font-mono text-[13px] text-jq-charcoal placeholder:text-jq-stone focus:border-jq-primary focus:outline-none focus:ring-1 focus:ring-jq-primary disabled:opacity-50"
      />

      {empty && (
        <p id={`${id}-error`} role="alert" className="text-[12px] text-jq-danger">
          {t('field.required', { locale })}
        </p>
      )}

      {invalid && !empty && (
        <p id={`${id}-error`} role="alert" className="text-[12px] text-jq-danger">
          <Trans
            t={t}
            i18nKey="field.missingLinkTicket"
            values={{ linkTicket: '{{link_ticket}}' }}
            components={{ code1: <code className="font-mono" /> }}
          />
        </p>
      )}

      {/* Live preview */}
      {value.trim().length > 0 && (
        <div id={`${id}-preview`} className="space-y-1">
          <p className="text-[11px] font-medium uppercase tracking-wide text-jq-stone">
            {t('field.previewLabel')}
          </p>
          <p className="rounded-sm border border-jq-sand/70 bg-jq-surface-1 px-3 py-2 text-[12px] leading-snug text-jq-graphite">
            {preview}
          </p>
        </div>
      )}

      {/* Character count */}
      <p
        id={`${id}-charcount`}
        className={`text-right text-[11px] ${overLimit ? 'font-medium text-jq-danger' : 'text-jq-stone'}`}
      >
        {t('field.charCount', { count: charCount, max: SMS_MAX_CHARS })}
        {overLimit && (
          <span aria-live="assertive">{t('field.overLimit', { max: SMS_MAX_CHARS })}</span>
        )}
      </p>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('org-sms-templates');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
